import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row, Typography } from 'antd';
import {
  Alert,
  Button,
  BUTTON_TYPES,
  ButtonGroup,
  Checkbox,
  DropDownMenu,
  RichArea,
  sanitizeRichAreaHtml,
  Select,
  Tooltip,
  User,
} from '_atoms';
import { LoadingCard } from '_molecules';
import type {
  ImproveInterviewFeedbackCommentPayload,
  ImproveInterviewFeedbackCommentResponse,
} from 'actions/jobActions';
import { ACTIONS, Can, UI } from 'permission';
import { ReactComponent as Ai } from 'dist/icons/ai.svg';
import { ReactComponent as Info } from 'dist/icons/info.svg';
import { ReactComponent as Undo } from 'dist/icons/undo.svg';
import { ReactComponent as Warning } from 'dist/icons/warning.svg';
import { useForm } from 'antd/es/form/Form';
import type { ButtonGroupOption } from '_atoms/ButtonGroup/types';
import type { Uuid } from 'store/reducers/types';
import { DECISION_OPTIONS, ENG_LEVEL_OPTIONS, EXPERIENCE_LEVEL_OPTIONS, REQUIRED_RULE } from './constants';
import type { FeedbackApprover, FeedbackFormValues } from './types';
import { canImproveCommentWithAi } from './utils';

interface FeedbackCreationProps {
  approver: FeedbackApprover;
  feedbackId: Uuid;
  initialValues: FeedbackFormValues;
  onCancel?: (() => void) | false;
  onFinish: (values: FeedbackFormValues, feedbackId: Uuid) => void;
  onImproveComment: (
    body: ImproveInterviewFeedbackCommentPayload,
    signal: AbortSignal
  ) => Promise<ImproveInterviewFeedbackCommentResponse | null | undefined>;
  isPosted?: boolean;
}

export const FeedbackCreation: React.FC<FeedbackCreationProps> = ({
  approver,
  feedbackId,
  initialValues,
  onCancel,
  onFinish,
  onImproveComment,
  isPosted,
}) => {
  const { t } = useTranslation();

  const [improvementStatus, setImprovementStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [previousComment, setPreviousComment] = useState<string>();
  const [richAreaState, setRichAreaState] = useState({
    initialValue: initialValues.comment_initial,
    revision: 0,
  });
  const improvementAbortController = useRef<AbortController>();
  const [form] = useForm();
  const comment = Form.useWatch('comment', form) as string | undefined;
  const isImprovementLoading = improvementStatus === 'loading';
  const hasImprovementError = improvementStatus === 'error';
  const improveWithAiDisabled = isImprovementLoading || !canImproveCommentWithAi(comment);
  const undoDisabled = isImprovementLoading || previousComment === undefined;

  useEffect(
    () => () => {
      improvementAbortController.current?.abort();
      improvementAbortController.current = undefined;
    },
    []
  );

  const onSave = (isDraft: boolean) => {
    form.setFieldValue('is_draft', isDraft);
    form.setFieldValue('is_edit', initialValues.is_edit);
    form.submit();
  };

  const handleImproveComment = async () => {
    if (!comment || isImprovementLoading) return;

    improvementAbortController.current?.abort();
    const controller = new AbortController();
    improvementAbortController.current = controller;
    setImprovementStatus('loading');

    try {
      const response = await onImproveComment(
        {
          current_comment: comment,
          ...(previousComment !== undefined && { prev_comment: previousComment }),
        },
        controller.signal
      );

      if (controller.signal.aborted) return;
      if (!response) {
        setImprovementStatus('idle');
        return;
      }

      const improvedComment = sanitizeRichAreaHtml(response.improved_comment);

      setPreviousComment(response.prev_comment);
      setRichAreaState(({ revision }) => ({
        initialValue: improvedComment,
        revision: revision + 1,
      }));
      form.setFieldValue('comment', improvedComment);
      setImprovementStatus('idle');
    } catch {
      if (!controller.signal.aborted) setImprovementStatus('error');
    } finally {
      if (improvementAbortController.current === controller) {
        improvementAbortController.current = undefined;
      }
    }
  };

  const onCancelImprovement = () => {
    improvementAbortController.current?.abort();
    improvementAbortController.current = undefined;
    setImprovementStatus('idle');
  };

  const onUndoImprovement = () => {
    if (previousComment === undefined) return;

    setRichAreaState(({ revision }) => ({
      initialValue: previousComment,
      revision: revision + 1,
    }));
    form.setFieldValue('comment', previousComment);
    setPreviousComment(undefined);
    setImprovementStatus('idle');
  };

  return (
    <div className="feedback-form">
      <User
        className="feedback-form__user"
        avatar={approver.profile.avatar}
        fullName={approver.full_name}
        roles={approver.roles}
      />
      <Form
        form={form}
        validateTrigger="onSubmit"
        initialValues={initialValues}
        onFinish={values => onFinish(values, feedbackId)}
      >
        <Row gutter={[16, 0]}>
          <Col lg={12} md={12} sm={22}>
            <ButtonGroup
              className="feedback-form__decision"
              label={t('translation:feedback_creation.label_decision')}
              options={DECISION_OPTIONS as unknown as ButtonGroupOption[]}
              itemProps={{ name: 'positive', rules: [REQUIRED_RULE] }}
            />
          </Col>
          <Col lg={12} md={12} sm={2}>
            <Tooltip label={t('translation:feedback_creation.label_decision_info')}>
              <Info className="feedback-form__decision-info" />
            </Tooltip>
          </Col>
        </Row>
        <Can I={ACTIONS.UPDATE} a={UI.FEEDBACK_ENGLISH_LEVEL}>
          <Row gutter={[16, 0]}>
            <Col lg={12} md={12} sm={24}>
              <Select
                label={t('translation:feedback_creation.label_assumed_english_level')}
                placeholder={t('translation:feedback_creation.ph_assumed_english_level')}
                labelInValue
                options={ENG_LEVEL_OPTIONS}
                itemProps={{ name: 'english_level', rules: [REQUIRED_RULE] }}
              />
            </Col>
          </Row>
        </Can>
        <Can I={ACTIONS.UPDATE} a={UI.FEEDBACK_EXPERIENCE_LEVEL}>
          <Row gutter={[16, 0]}>
            <Col lg={12} md={12} sm={24}>
              <Select
                label={t('translation:feedback_creation.label_assumed_experience_level')}
                placeholder={t('translation:feedback_creation.ph_assumed_experience_level')}
                labelInValue
                options={EXPERIENCE_LEVEL_OPTIONS}
                itemProps={{ name: 'level', rules: [REQUIRED_RULE] }}
              />
            </Col>
          </Row>
        </Can>
        <Row className="feedback-form__comment-row">
          <Col span={24}>
            <Form.Item className="rich-area-item" name="comment" rules={[REQUIRED_RULE]}>
              <RichArea
                key={richAreaState.revision}
                initialValue={richAreaState.initialValue}
                label={t('translation:feedback_creation.label_comment')}
                placeholder={t('translation:feedback_creation.ph_comment')}
                maxLength={10000}
              />
            </Form.Item>
          </Col>
          <Col className="feedback-form__comment-menu">
            <DropDownMenu>
              <li key="improve_ai" role="none" onClick={improveWithAiDisabled ? undefined : handleImproveComment}>
                <Typography.Text disabled={improveWithAiDisabled}>
                  {t('translation:feedback_creation.button_improve_with_ai')}
                </Typography.Text>
              </li>
              <li key="undo" role="none" onClick={undoDisabled ? undefined : onUndoImprovement}>
                <Typography.Text disabled={undoDisabled}>
                  {t('translation:feedback_creation.button_undo')}
                </Typography.Text>
              </li>
            </DropDownMenu>
          </Col>
        </Row>
        <Checkbox style={{ display: 'none' }} itemProps={{ name: 'is_draft', noStyle: true }} />
        <Checkbox style={{ display: 'none' }} itemProps={{ name: 'is_edit', noStyle: true }} />

        {isImprovementLoading && (
          <LoadingCard
            title={t('translation:feedback_creation.ai_loading_title')}
            description={t('translation:feedback_creation.ai_loading_description')}
            action={
              <Button type={BUTTON_TYPES.SECONDARY} variant="grey" onClick={onCancelImprovement}>
                {t('translation:feedback_creation.button_cancel')}
              </Button>
            }
          />
        )}

        {hasImprovementError && (
          <Alert type="error" icon={<Warning />} message={t('translation:feedback_creation.ai_error')} showIcon />
        )}

        <div className="feedback-form__footer">
          <div className="feedback-form__ai-actions">
            <Button
              type={BUTTON_TYPES.SECONDARY}
              icon={<Ai />}
              disabled={improveWithAiDisabled}
              onClick={handleImproveComment}
            >
              {t('translation:feedback_creation.button_improve_with_ai')}
            </Button>
            <Button
              type={BUTTON_TYPES.SECONDARY}
              variant="grey"
              icon={<Undo />}
              disabled={undoDisabled}
              onClick={onUndoImprovement}
            >
              {t('translation:feedback_creation.button_undo')}
            </Button>
          </div>
          <div className="feedback-form__actions">
            {onCancel && (
              <Button onClick={onCancel} type={BUTTON_TYPES.GHOST} disabled={isImprovementLoading}>
                {t('translation:feedback_creation.button_cancel')}
              </Button>
            )}
            {!isPosted && (
              <Button type={BUTTON_TYPES.SECONDARY} onClick={() => onSave(true)} disabled={isImprovementLoading}>
                {t('translation:feedback_creation.button_save_draft')}
              </Button>
            )}
            <Button type={BUTTON_TYPES.PRIMARY} onClick={() => onSave(false)} disabled={isImprovementLoading}>
              {t('translation:feedback_creation.button_save')}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};
